import { Button, Table } from "antd";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import EmptyWrapper from "../../common/EmptyWrapper";
import { KontaxUserContext } from "../../contexts/KontaxUserContext";
import { formatPaymentFrequency } from "../../../utils/paymentFrequency";
import { ZmUserStatus } from "../../../types";
import ZmUserDetails from "./ZmUserDetails";
import { IZmUser, ZmFilterOptions } from "./types";

const ZmTable = ({
  users,
  loading,
  filterOptions,
  page,
  hasUsers,
  onSelectCustomer,
  onPreviousPage,
  onNextPage,
}: {
  users: IZmUser[];
  loading: boolean;
  filterOptions: ZmFilterOptions;
  page: number;
  hasUsers: boolean;
  onSelectCustomer: (email: string) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
}) => {
  const { t } = useTranslation("ZmView");
  // Expanding a row selects the customer for the ELSTER tab (the `?email=`
  // param) — it does not filter the list. SMT-1354.
  const handleRowExpand = (expanded: boolean, record: IZmUser) => {
    if (expanded) {
      onSelectCustomer(record.email);
    }
  };
  const columns = useMemo(
    () => [
      {
        title: t("table.name"),
        key: "name",
        width: "60%",
        render: (row: IZmUser) => {
          const displayName =
            row.legalName?.trim() ||
            [row.firstName, row.lastName].filter(Boolean).join(" ").trim() ||
            "-";

          return (
            <>
              <div>{displayName}</div>
              <div>{row.email}</div>
            </>
          );
        },
      },
      {
        title: t("table.vatFrequency"),
        key: "frequency",
        width: "20%",
        render: (row: IZmUser) => formatPaymentFrequency(row.frequency),
      },
      {
        title: t("table.status"),
        key: "status",
        width: "20%",
        render: (row: IZmUser) => {
          const statusKey = Object.entries(ZmUserStatus).find(
            ([, label]) => label === row.status
          )?.[0];

          return statusKey ? t(`status.${statusKey}`) : row.status;
        },
      },
    ],
    [t]
  );

  const renderExpandedRow = useCallback(
    (row: IZmUser) => (
      <KontaxUserContext email={row.email}>
        <ZmUserDetails user={row} filterOptions={filterOptions} />
      </KontaxUserContext>
    ),
    [filterOptions]
  );

  return (
    <>
      <Table
        columns={columns}
        dataSource={users}
        loading={loading}
        bordered
        expandable={{
          expandedRowRender: renderExpandedRow,
          expandRowByClick: true,
          onExpand: handleRowExpand,
        }}
        pagination={false}
        tableLayout="fixed"
        scroll={{ x: "100%" }}
        rowKey="id"
        locale={{
          emptyText: (
            <EmptyWrapper
              description={
                filterOptions.email
                  ? t("table.noUsersForEmail")
                  : t("table.noUsers")
              }
            />
          ),
        }}
      />
      {(hasUsers || page > 1) && (
        <>
          {page > 1 && (
            <Button onClick={onPreviousPage} type="primary">
              {t("table.previous")}
            </Button>
          )}
          <span style={{ paddingRight: "10px" }}>
            {t("table.page", { page })}
          </span>
          <Button onClick={onNextPage} type="primary">
            {t("table.next")}
          </Button>
        </>
      )}
    </>
  );
};

export default ZmTable;
